'use client'

import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { addDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Checkbox } from 'components/ui/checkbox'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Separator } from 'components/ui/separator'
import { Textarea } from 'components/ui/textarea'
import { toast } from 'sonner'
import { auth, db } from '../../../../firebase'
import { useUser } from 'components/context/user-context'

const AUDIT_COLLECTION = 'auditLogs'

const SDG_GOALS = [
  { number: 1, name: 'No Poverty', color: 'oklch(0.45 0.2 20)' },
  { number: 2, name: 'Zero Hunger', color: 'oklch(0.65 0.18 80)' },
  { number: 3, name: 'Good Health & Well-being', color: 'oklch(0.52 0.2 140)' },
  { number: 4, name: 'Quality Education', color: 'oklch(0.58 0.18 250)' },
  { number: 5, name: 'Gender Equality', color: 'oklch(0.55 0.22 10)' },
  { number: 6, name: 'Clean Water & Sanitation', color: 'oklch(0.7 0.2 220)' },
  { number: 7, name: 'Affordable & Clean Energy', color: 'oklch(0.78 0.18 90)' },
  { number: 8, name: 'Decent Work & Growth', color: 'oklch(0.6 0.2 30)' },
  { number: 9, name: 'Industry & Innovation', color: 'oklch(0.62 0.16 260)' },
  { number: 10, name: 'Reduced Inequalities', color: 'oklch(0.52 0.19 300)' },
  { number: 11, name: 'Sustainable Cities', color: 'oklch(0.65 0.2 40)' },
  { number: 12, name: 'Responsible Consumption', color: 'oklch(0.6 0.14 70)' },
  { number: 13, name: 'Climate Action', color: 'oklch(0.58 0.18 170)' },
  { number: 14, name: 'Life Below Water', color: 'oklch(0.6 0.2 210)' },
  { number: 15, name: 'Life on Land', color: 'oklch(0.57 0.2 130)' },
  { number: 16, name: 'Peace & Justice', color: 'oklch(0.55 0.16 280)' },
  { number: 17, name: 'Partnerships', color: 'oklch(0.58 0.18 330)' },
]

const strategicPillars = [
  'Health Innovation',
  'Sustainable Development',
  'Digital Transformation',
  'Food Security',
  'Community Engagement',
  'Climate Action',
  'Youth Empowerment',
  'Education Quality',
]

const defaultBudgetItems = [
  { id: 'new-1', category: 'Personnel', description: '', quantity: 1, unitCost: 0, total: 0 },
  { id: 'new-2', category: 'Equipment', description: '', quantity: 1, unitCost: 0, total: 0 },
  { id: 'new-3', category: 'Travel', description: '', quantity: 1, unitCost: 0, total: 0 },
  { id: 'new-4', category: 'Training', description: '', quantity: 1, unitCost: 0, total: 0 },
  { id: 'new-5', category: 'Overheads', description: '', quantity: 1, unitCost: 0, total: 0 },
]

function formatCurrency(value, currency) {
  const amount = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: currency || 'KES',
    maximumFractionDigits: 0,
  }).format(amount)
}

export default function NewProjectPage() {
  const navigate = useNavigate()
  const { canEdit } = useUser()
  const currentUser = auth.currentUser
  const [creating, setCreating] = React.useState(false)
  const [createWorkplanTemplate, setCreateWorkplanTemplate] = React.useState(true)
  const [availableDepartments, setAvailableDepartments] = React.useState([])
  const [availableUsers, setAvailableUsers] = React.useState([])
  const [availableDonors, setAvailableDonors] = React.useState([])
  const [formData, setFormData] = React.useState({
    title: '',
    type: 'donor_funded',
    status: 'planning',
    department: '',
    description: '',
    startDate: '',
    endDate: '',
    currency: 'KES',
    donors: [],
    principalInvestigatorId: currentUser?.uid || '',
    principalInvestigatorName: currentUser?.displayName || currentUser?.email || '',
    projectManagerId: '',
    projectManagerName: '',
    projectMembers: [],
    sdgMappings: [],
    strategicPillars: [],
    budgetItems: defaultBudgetItems,
  })
  const [selectedMemberId, setSelectedMemberId] = React.useState('')
  const [selectedDonor, setSelectedDonor] = React.useState('')

  React.useEffect(() => {
    let isMounted = true

    async function loadDepartments() {
      try {
        const snapshot = await getDocs(collection(db, 'Departments'))
        if (!isMounted) return
        const departments = snapshot.docs
          .map((docItem) => docItem.data()?.schools)
          .filter(Boolean)
        setAvailableDepartments(Array.from(new Set(departments)))
      } catch {
        setAvailableDepartments([])
      }
    }

    async function loadUsers() {
      try {
        const snapshot = await getDocs(collection(db, 'users'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setAvailableUsers(records)
      } catch {
        setAvailableUsers([])
      }
    }

    async function loadDonors() {
      try {
        const snapshot = await getDocs(collection(db, 'stakeholders'))
        if (!isMounted) return
        const donorNames = snapshot.docs
          .map((docItem) => {
            const data = docItem.data() || {}
            return data.organization || data.name || data.fullName
          })
          .filter(Boolean)
          .map((name) => String(name).trim())
        setAvailableDonors(Array.from(new Set(donorNames)).sort())
      } catch {
        setAvailableDonors([])
      }
    }

    loadDepartments()
    loadUsers()
    loadDonors()

    return () => {
      isMounted = false
    }
  }, [])

  const grandTotal = React.useMemo(
    () => formData.budgetItems.reduce((sum, item) => sum + item.quantity * item.unitCost, 0),
    [formData.budgetItems],
  )

  const updateBudgetItem = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      budgetItems: prev.budgetItems.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
              total:
                field === 'quantity' || field === 'unitCost'
                  ? (field === 'quantity' ? Number(value) : item.quantity) *
                    (field === 'unitCost' ? Number(value) : item.unitCost)
                  : item.total,
            }
          : item,
      ),
    }))
  }

  const addBudgetLine = () => {
    setFormData((prev) => ({
      ...prev,
      budgetItems: [
        ...prev.budgetItems,
        { id: `new-${Date.now()}`, category: 'Other', description: '', quantity: 1, unitCost: 0, total: 0 },
      ],
    }))
  }

  const removeBudgetLine = (index) => {
    setFormData((prev) => ({
      ...prev,
      budgetItems: prev.budgetItems.filter((_, i) => i !== index),
    }))
  }

  const addProjectMember = () => {
    if (!selectedMemberId) return
    const selected = availableUsers.find((user) => (user.userId || user.id) === selectedMemberId)
    if (!selected) return
    const memberId = selected.userId || selected.id
    setFormData((prev) => {
      if (prev.projectMembers.some((member) => member.id === memberId)) {
        return prev
      }
      return {
        ...prev,
        projectMembers: [
          ...prev.projectMembers,
          {
            id: memberId,
            name: selected.fullName || selected.name || selected.email || 'User',
          },
        ],
      }
    })
    setSelectedMemberId('')
  }

  const removeProjectMember = (memberId) => {
    setFormData((prev) => ({
      ...prev,
      projectMembers: prev.projectMembers.filter((member) => member.id !== memberId),
    }))
  }

  const addDonor = () => {
    if (!selectedDonor) return
    setFormData((prev) => {
      if (prev.donors.some((donor) => donor.toLowerCase() === selectedDonor.toLowerCase())) {
        return prev
      }
      return { ...prev, donors: [...prev.donors, selectedDonor] }
    })
    setSelectedDonor('')
  }

  const removeDonor = (name) => {
    setFormData((prev) => ({
      ...prev,
      donors: prev.donors.filter((donor) => donor !== name),
    }))
  }

  const toggleSDG = (sdgNumber) => {
    setFormData((prev) => ({
      ...prev,
      sdgMappings: prev.sdgMappings.includes(sdgNumber)
        ? prev.sdgMappings.filter((value) => value !== sdgNumber)
        : [...prev.sdgMappings, sdgNumber],
    }))
  }

  const togglePillar = (pillar) => {
    setFormData((prev) => ({
      ...prev,
      strategicPillars: prev.strategicPillars.includes(pillar)
        ? prev.strategicPillars.filter((value) => value !== pillar)
        : [...prev.strategicPillars, pillar],
    }))
  }

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a project title')
      return
    }
    if (creating) return
    setCreating(true)

    try {
      const budgetItems = formData.budgetItems.map((item) => ({
        ...item,
        total: item.quantity * item.unitCost,
      }))

      const payload = {
        title: formData.title.trim(),
        type: formData.type,
        status: formData.status,
        department: formData.department || 'Not specified',
        description: formData.description || '',
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        currency: formData.currency,
        donors: formData.donors || [],
        totalBudget: grandTotal,
        principalInvestigatorId: formData.principalInvestigatorId || currentUser?.uid || 'unknown',
        principalInvestigatorName:
          formData.principalInvestigatorName || currentUser?.displayName || 'Unknown',
        projectManagerId: formData.projectManagerId || '',
        projectManagerName: formData.projectManagerName || '',
        projectMembers: formData.projectMembers || [],
        sdgMappings: formData.sdgMappings,
        strategicPillars: formData.strategicPillars,
        budgetItems,
        createWorkplanTemplate,
      }

      const projectRef = await addDoc(collection(db, 'projects'), {
        ...payload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })

      try {
        await addDoc(collection(db, AUDIT_COLLECTION), {
          timestamp: serverTimestamp(),
          userId: currentUser?.uid || 'system',
          userDisplayName: currentUser?.displayName || currentUser?.email || 'System',
          action: 'CREATE',
          entity: 'Project',
          entityId: projectRef.id,
          changes: {
            before: '',
            after: `Created project "${payload.title}"`,
          },
        })
      } catch (error) {
        console.warn('Failed to write audit log', error)
      }

      toast.success('Project created successfully!', {
        description: `"${formData.title}" has been added to the portfolio.`,
      })
      navigate('/dashboard/projects')
      return projectRef.id
    } catch (error) {
      toast.error('Failed to create project', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setCreating(false)
    }
  }

  if (!canEdit) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-dashed border-border p-6 text-center">
          <h2 className="text-lg font-semibold text-foreground">Read-only access</h2>
          <p className="text-sm text-muted-foreground mt-2">
            You do not have permission to create new projects.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild className="size-8">
          <Link to="/dashboard/projects">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            New Project
          </h1>
          <p className="text-sm text-muted-foreground">
            Create a project from scratch
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Basic Information</CardTitle>
          <CardDescription>Define the core details and leadership for this project</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Project Title *</Label>
            <Input
              id="title"
              placeholder="Enter project title"
              value={formData.title}
              onChange={(event) => setFormData({ ...formData, title: event.target.value })}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Project Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Project Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="donor_funded">Donor Funded</SelectItem>
                  <SelectItem value="internal">Internal (University-Funded)</SelectItem>
                  <SelectItem value="collaborative">Collaborative / Consortium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.type === 'donor_funded' ? (
              <div className="space-y-2 sm:col-span-3">
                <Label>Donor Names</Label>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Select
                      value={selectedDonor}
                      onValueChange={(value) => setSelectedDonor(value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select donor" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDonors.length === 0 ? (
                          <SelectItem value="no-donors" disabled>
                            No donors available
                          </SelectItem>
                        ) : (
                          availableDonors.map((donor) => (
                            <SelectItem key={donor} value={donor}>
                              {donor}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="secondary" onClick={addDonor} className="sm:w-auto">
                      Add Donor
                    </Button>
                  </div>
                  {formData.donors.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.donors.map((donor) => (
                        <button
                          key={donor}
                          type="button"
                          onClick={() => removeDonor(donor)}
                          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-foreground hover:bg-muted/60"
                        >
                          {donor}
                          <span className="text-muted-foreground">×</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Add at least one donor.</p>
                  )}
                </div>
              </div>
            ) : null}
            <div className="space-y-2">
              <Label>Department / School</Label>
              <Select
                value={formData.department}
                onValueChange={(value) => setFormData({ ...formData, department: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {availableDepartments.length === 0 ? (
                    <SelectItem value="no-departments" disabled>
                      No departments available
                    </SelectItem>
                  ) : (
                    availableDepartments.map((department) => (
                      <SelectItem key={department} value={department}>
                        {department}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pi">Principal Investigator</Label>
            <Select
              value={formData.principalInvestigatorId}
              onValueChange={(value) => {
                const selected = availableUsers.find((user) => (user.userId || user.id) === value)
                setFormData((prev) => ({
                  ...prev,
                  principalInvestigatorId: value,
                  principalInvestigatorName: selected?.fullName || selected?.name || selected?.email || '',
                }))
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select principal investigator" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.length === 0 ? (
                  <SelectItem value="no-users" disabled>
                    No users available
                  </SelectItem>
                ) : (
                  availableUsers.map((user) => (
                    <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                      {user.fullName || user.name || user.email || 'User'}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="pm">Project Manager</Label>
              <Select
                value={formData.projectManagerId}
                onValueChange={(value) => {
                  const selected = availableUsers.find((user) => (user.userId || user.id) === value)
                  setFormData((prev) => ({
                    ...prev,
                    projectManagerId: value,
                    projectManagerName: selected?.fullName || selected?.name || selected?.email || '',
                  }))
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project manager" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <SelectItem value="no-users" disabled>
                      No users available
                    </SelectItem>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                        {user.fullName || user.name || user.email || 'User'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="members">Project Members</Label>
              <div className="flex gap-2">
                <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                  <SelectTrigger id="members">
                    <SelectValue placeholder="Select member" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.length === 0 ? (
                      <SelectItem value="no-users" disabled>
                        No users available
                      </SelectItem>
                    ) : (
                      availableUsers.map((user) => (
                        <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                          {user.fullName || user.name || user.email || 'User'}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addProjectMember}
                  disabled={!selectedMemberId}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.projectMembers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No members added.</p>
                ) : (
                  formData.projectMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs text-foreground"
                    >
                      <span>{member.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-5"
                        onClick={() => removeProjectMember(member.id)}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">Description</Label>
            <Textarea
              id="desc"
              placeholder="Brief description of the project objectives and outcomes"
              rows={4}
              value={formData.description}
              onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="start">Start Date</Label>
              <Input
                id="start"
                type="date"
                value={formData.startDate}
                onChange={(event) => setFormData({ ...formData, startDate: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end">End Date</Label>
              <Input
                id="end"
                type="date"
                value={formData.endDate}
                onChange={(event) => setFormData({ ...formData, endDate: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="KES">KES - Kenya Shillings</SelectItem>
                  <SelectItem value="USD">USD - US Dollars</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Budget Breakdown</CardTitle>
            <CardDescription>Detail the budget by line item</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={addBudgetLine}>
            <Plus className="mr-1 size-3.5" /> Add Line
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.budgetItems.map((item, index) => (
            <div key={item.id}>
              <div className="mb-2 flex items-center gap-2">
                <p className="flex-1 text-sm font-medium text-foreground">{item.category}</p>
                {formData.budgetItems.length > 1 ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => removeBudgetLine(index)}
                  >
                    <Trash2 className="size-3.5 text-muted-foreground" />
                  </Button>
                ) : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="sm:col-span-2">
                  <Input
                    placeholder="Description"
                    value={item.description}
                    onChange={(event) => updateBudgetItem(index, 'description', event.target.value)}
                  />
                </div>
                <Input
                  type="number"
                  placeholder="Qty"
                  min={0}
                  value={item.quantity || ''}
                  onChange={(event) =>
                    updateBudgetItem(index, 'quantity', parseInt(event.target.value, 10) || 0)
                  }
                />
                <Input
                  type="number"
                  placeholder="Unit Cost"
                  min={0}
                  value={item.unitCost || ''}
                  onChange={(event) =>
                    updateBudgetItem(index, 'unitCost', parseFloat(event.target.value) || 0)
                  }
                />
              </div>
              <p className="mt-1 text-right text-xs text-muted-foreground">
                Subtotal: {formatCurrency(item.quantity * item.unitCost, formData.currency)}
              </p>
              {index < formData.budgetItems.length - 1 ? <Separator className="mt-3" /> : null}
            </div>
          ))}
          <Separator />
          <div className="flex items-center justify-between">
            <span className="font-semibold text-foreground">Grand Total</span>
            <span className="text-lg font-bold text-foreground">
              {formatCurrency(grandTotal, formData.currency)}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">SDG & Strategic Pillar Mapping</CardTitle>
          <CardDescription>Map the project to relevant SDGs and JOOUST strategic pillars</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="mb-3 block text-sm font-semibold">Select Relevant SDGs</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {SDG_GOALS.map((sdg) => {
                const selected = formData.sdgMappings.includes(sdg.number)
                return (
                  <button
                    key={sdg.number}
                    onClick={() => toggleSDG(sdg.number)}
                    className={`rounded-lg border p-2.5 text-left text-xs transition-all ${
                      selected ? 'border-2 bg-primary/5' : 'border hover:bg-muted/50'
                    }`}
                    style={selected ? { borderColor: sdg.color } : {}}
                  >
                    <span className="font-bold" style={selected ? { color: sdg.color } : {}}>
                      SDG {sdg.number}
                    </span>
                    <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">
                      {sdg.name}
                    </p>
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <Label className="mb-3 block text-sm font-semibold">JOOUST Strategic Pillars</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {strategicPillars.map((pillar) => {
                const selected = formData.strategicPillars.includes(pillar)
                return (
                  <button
                    key={pillar}
                    onClick={() => togglePillar(pillar)}
                    className={`rounded-lg border p-2.5 text-xs transition-all ${
                      selected ? 'border-2 bg-primary/5' : 'border hover:bg-muted/50'
                    }`}
                    style={selected ? { borderColor: 'hsl(var(--secondary))' } : {}}
                  >
                    <span className={selected ? 'font-bold' : 'text-muted-foreground'}>
                      {pillar}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Setup Options</CardTitle>
          <CardDescription>Use standard templates to bootstrap this project</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3">
            <Checkbox
              id="workplan-template"
              checked={createWorkplanTemplate}
              onCheckedChange={(value) => setCreateWorkplanTemplate(value === true)}
            />
            <Label htmlFor="workplan-template" className="cursor-pointer text-sm">
              Create a default workplan template for this project.
            </Label>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={creating}>
          {creating ? 'Creating...' : 'Create Project'}
        </Button>
      </div>
    </div>
  )
}
